import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'
import useStorage from 'src/sdk/hooks/useStorage'
import { axiosClient } from 'src/services/axiosInstance'
import { unmaskCEP } from 'src/utils/formatCEP'

import type {
  TurboPickupError,
  TurboPickupModalState,
  TurboPickupSelection,
  TurboPickupStore,
} from './types'

const STORAGE_KEY = 'turboPickupSelectedStore'

export function useTurboPickup() {
  const { postalCode, setPostalCode } = useAddressContext()
  const { storageGetItem, storageSetItem, storageRemoveItem } = useStorage()

  const [modalState, setModalState] = useState<TurboPickupModalState>(
    postalCode ? 'store-list' : 'zip-input'
  )

  const [stores, setStores] = useState<TurboPickupStore[]>([])
  const [error, setError] = useState<TurboPickupError | null>(null)
  const [savedSelection, setSavedSelection] =
    useState<TurboPickupSelection | null>(null)

  useEffect(() => {
    const raw = storageGetItem(STORAGE_KEY, 'local')

    if (raw) {
      try {
        setSavedSelection(JSON.parse(raw) as TurboPickupSelection)
      } catch {
        // corrupted value — ignore
      }
    }
  }, [storageGetItem])

  const fetchStores = useCallback(async (zipCode: string) => {
    setModalState('loading')
    setError(null)

    try {
      const { data } = await axiosClient.get(
        '/api/deliveryPromise/pickupInPoint',
        { params: { zipCode, sc: '3' } }
      )

      const storeList: TurboPickupStore[] = data?.pickupPointDistances ?? []

      if (storeList.length === 0) {
        const errorType: TurboPickupError = 'NO_STORES'

        setError(errorType)
        setModalState('error')

        return {
          status: 'error',
          error: errorType,
        }
      }

      setStores(storeList)
      setModalState('store-list')

      return {
        status: 'success',
      }
    } catch (caughtError) {
      const responseCode = axios.isAxiosError(caughtError)
        ? (caughtError.response?.data as { code?: string } | undefined)?.code
        : undefined

      const isInvalidZip =
        axios.isAxiosError(caughtError) &&
        (caughtError.response?.status === 400 || responseCode === 'INVALID_ZIP')

      const errorType: TurboPickupError = isInvalidZip
        ? 'INVALID_ZIP'
        : 'API_ERROR'

      setError(errorType)
      setModalState('error')

      return {
        status: 'error',
        error: errorType,
      }
    }
  }, [])

  const selectStore = useCallback(
    (store: TurboPickupStore) => {
      const selection: TurboPickupSelection = {
        pickupId: store.pickupId,
        pickupName: store.pickupName,
      }

      storageSetItem(STORAGE_KEY, JSON.stringify(selection), 'local')

      const zipCode = unmaskCEP(postalCode ?? '')
      const params = new URLSearchParams({
        'marca-propria': 'marcas-decathlon',
        facets: 'marca-propria,pickupPoint',
        sort: 'score_desc',
        page: '0',
        zipCode,
        pickupPoint: store.pickupId,
      })

      window.location.href = `/pesquisa/?${params.toString()}`
    },
    [postalCode, storageSetItem]
  )

  const clearSelection = useCallback(() => {
    storageRemoveItem(STORAGE_KEY, 'local')
    setSavedSelection(null)
  }, [storageRemoveItem])

  return {
    postalCode,
    setPostalCode,
    savedSelection,
    modalState,
    stores,
    error,
    fetchStores,
    selectStore,
    clearSelection,
    setModalState,
  }
}
